import jsPDF from "jspdf";

/**
 * Loads a remote image as a base64 dataURL through a Canvas, so jsPDF can
 * embed it without CORS issues.
 */
async function urlToDataUrl(url) {
  if (!url) return null;
  try {
    const res = await fetch(url, { mode: "cors" });
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Generate an A4 fidelization poster (PDF) for a restaurant.
 *
 * @param {Object} opts
 * @param {string} opts.restaurantName     - Display name of the restaurant.
 * @param {string} [opts.logoUrl]          - Optional remote URL of the logo.
 * @param {string} opts.qrDataUrl          - QR code as a data URL (base64 PNG).
 * @param {string} opts.portalUrl          - Public portal URL (printed at the bottom).
 * @param {number} [opts.pointsPerEuro=1]  - Points earned per EUR spent.
 * @param {number|null} [opts.firstRewardPoints=null] - Points required for the cheapest reward.
 */
export async function generateLoyaltyPosterPDF({
  restaurantName = "Tu restaurante",
  logoUrl = null,
  qrDataUrl,
  portalUrl,
  pointsPerEuro = 1,
  firstRewardPoints = null,
}) {
  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

  const pageW = pdf.internal.pageSize.getWidth();   // 210
  const pageH = pdf.internal.pageSize.getHeight();  // 297

  const orange = [249, 115, 22];   // #F97316 — primary brand
  const dark = [15, 23, 42];       // #0F172A — slate-900
  const slate = [100, 116, 139];   // #64748B

  // Background — soft top accent
  pdf.setFillColor(255, 247, 237); // amber-50
  pdf.rect(0, 0, pageW, 60, "F");

  // Logo (top center) — optional
  let cursorY = 18;
  const logoData = await urlToDataUrl(logoUrl);
  if (logoData) {
    try {
      const logoSize = 22;
      pdf.addImage(logoData, "PNG", (pageW - logoSize) / 2, cursorY, logoSize, logoSize, undefined, "FAST");
      cursorY += logoSize + 6;
    } catch {
      // Fallback: skip logo silently
      cursorY += 6;
    }
  } else {
    cursorY += 4;
  }

  // Restaurant name
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(20);
  pdf.setTextColor(...dark);
  pdf.text(restaurantName, pageW / 2, cursorY, { align: "center" });

  // Hero title
  cursorY = 78;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(34);
  pdf.setTextColor(...orange);
  pdf.text("Únete a nuestro", pageW / 2, cursorY, { align: "center" });
  pdf.text("programa de puntos", pageW / 2, cursorY + 12, { align: "center" });

  // Subtitle
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(13);
  pdf.setTextColor(...slate);
  pdf.text("Escanea el QR con tu móvil y empieza a ganar recompensas",
    pageW / 2, cursorY + 24, { align: "center" });

  // QR — big centered
  const qrSize = 90;
  const qrX = (pageW - qrSize) / 2;
  const qrY = 132;
  // White panel behind the QR for high contrast on prints
  pdf.setFillColor(255, 255, 255);
  pdf.setDrawColor(...orange);
  pdf.setLineWidth(1.2);
  pdf.roundedRect(qrX - 6, qrY - 6, qrSize + 12, qrSize + 12, 4, 4, "FD");
  if (qrDataUrl) {
    pdf.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize, undefined, "FAST");
  }

  // Three benefit chips below the QR
  const chipY = qrY + qrSize + 18;
  const chipH = 22;
  const chipsArea = 150;
  const chipW = (chipsArea - 12) / 3;
  const chipStart = (pageW - chipsArea) / 2;
  const chips = [
    { v: `${pointsPerEuro} pt/€`, l: "Por cada euro" },
    { v: firstRewardPoints != null ? `${firstRewardPoints} pts` : "Recompensas",
      l: firstRewardPoints != null ? "Primera recompensa" : "Cumple, gana, canjea" },
    { v: "Gratis", l: "Sin coste para ti" },
  ];
  chips.forEach((c, i) => {
    const x = chipStart + i * (chipW + 6);
    pdf.setFillColor(255, 247, 237);
    pdf.setDrawColor(254, 215, 170);
    pdf.setLineWidth(0.4);
    pdf.roundedRect(x, chipY, chipW, chipH, 3, 3, "FD");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.setTextColor(...orange);
    pdf.text(c.v, x + chipW / 2, chipY + 9, { align: "center" });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(...slate);
    pdf.text(c.l, x + chipW / 2, chipY + 16, { align: "center" });
  });

  // Footer — restaurant + URL
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(...slate);
  if (portalUrl) {
    pdf.text(portalUrl, pageW / 2, pageH - 16, { align: "center" });
  }
  pdf.setFontSize(8);
  pdf.text("Fidelización gestionada con MenuGenius", pageW / 2, pageH - 10, { align: "center" });

  const filename = `poster-fidelizacion-${restaurantName.toLowerCase().replace(/\s+/g, "-")}.pdf`;
  pdf.save(filename);
}



/**
 * Generate a "table tent" (10x15 cm portrait) PDF for tabletop standees.
 * Same content as the poster but compact and meant to live on a table.
 */
export async function generateLoyaltyTableTentPDF({
  restaurantName = "Tu restaurante",
  logoUrl = null,
  qrDataUrl,
  pointsPerEuro = 1,
  firstRewardPoints = null,
}) {
  // 10 x 15 cm = 100 x 150 mm
  const pdf = new jsPDF({ unit: "mm", format: [100, 150], orientation: "portrait" });
  const pageW = 100;
  const orange = [249, 115, 22];
  const dark = [15, 23, 42];
  const slate = [100, 116, 139];

  // Top accent
  pdf.setFillColor(255, 247, 237);
  pdf.rect(0, 0, pageW, 28, "F");

  // Logo top-left, name centered
  let cy = 8;
  const logoData = await urlToDataUrl(logoUrl);
  if (logoData) {
    try { pdf.addImage(logoData, "PNG", 6, 4, 14, 14, undefined, "FAST"); } catch { /* skip */ }
  }
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  pdf.setTextColor(...dark);
  pdf.text(restaurantName, pageW / 2, cy + 6, { align: "center" });

  // Hero
  cy = 36;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(15);
  pdf.setTextColor(...orange);
  pdf.text("Escanea y gana", pageW / 2, cy, { align: "center" });
  pdf.text("recompensas", pageW / 2, cy + 6, { align: "center" });

  // QR
  const qrSize = 50;
  const qrX = (pageW - qrSize) / 2;
  const qrY = 56;
  pdf.setFillColor(255, 255, 255);
  pdf.setDrawColor(...orange);
  pdf.setLineWidth(0.8);
  pdf.roundedRect(qrX - 3, qrY - 3, qrSize + 6, qrSize + 6, 2, 2, "FD");
  if (qrDataUrl) {
    pdf.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize, undefined, "FAST");
  }

  // Mini-stats below
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.setTextColor(...orange);
  const statY = qrY + qrSize + 10;
  pdf.text(`${pointsPerEuro} pt/€`, pageW / 2, statY, { align: "center" });
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(...slate);
  if (firstRewardPoints != null) {
    pdf.text(`Primera recompensa con ${firstRewardPoints} puntos · sin coste`, pageW / 2, statY + 5, { align: "center" });
  } else {
    pdf.text("Sin coste para ti · únete y empieza a ganar", pageW / 2, statY + 5, { align: "center" });
  }

  pdf.setFontSize(7);
  pdf.text("Gestionado con MenuGenius", pageW / 2, 144, { align: "center" });

  pdf.save(`tarjeta-mesa-fidelizacion-${restaurantName.toLowerCase().replace(/\s+/g, "-")}.pdf`);
}

/**
 * Loads an image into an HTMLImageElement (with crossorigin) so we can paint
 * it onto a Canvas. Used for PNG generation (Stories / IG).
 */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/**
 * Generate a 1080×1920 PNG ready for Instagram Stories / WhatsApp Status with
 * the brand orange gradient + logo + QR + promo line.
 */
export async function generateLoyaltyStoryPNG({
  restaurantName = "Tu restaurante",
  logoUrl = null,
  qrDataUrl,
  pointsPerEuro = 1,
  firstRewardPoints = null,
}) {
  const W = 1080, H = 1920;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background gradient (slate-900 → orange tinted)
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, "#0F172A");
  grad.addColorStop(0.55, "#1F1B16");
  grad.addColorStop(1, "#7C2D12");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Orange accent blobs (decorative)
  ctx.fillStyle = "rgba(249, 115, 22, 0.18)";
  ctx.beginPath(); ctx.arc(900, 240, 280, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(120, 1700, 320, 0, Math.PI * 2); ctx.fill();

  // Logo + name top
  const logoImg = await loadImage(await urlToDataUrl(logoUrl));
  let topY = 200;
  if (logoImg) {
    const lw = 140;
    ctx.drawImage(logoImg, (W - lw) / 2, topY, lw, lw);
    topY += lw + 30;
  }
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "700 56px system-ui, -apple-system, Segoe UI, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(restaurantName, W / 2, topY + 40);

  // Hero
  ctx.fillStyle = "#F97316";
  ctx.font = "800 86px system-ui, -apple-system, Segoe UI, sans-serif";
  ctx.fillText("Únete y", W / 2, 600);
  ctx.fillText("acumula puntos", W / 2, 700);

  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.font = "400 38px system-ui, -apple-system, Segoe UI, sans-serif";
  ctx.fillText("Escanea el QR con tu móvil", W / 2, 770);

  // QR — white card behind
  const qrImg = await loadImage(qrDataUrl);
  const qrSize = 580;
  const qrX = (W - qrSize) / 2;
  const qrY = 830;
  // white card
  ctx.fillStyle = "#FFFFFF";
  ctx.beginPath();
  // rounded rect
  const r = 28, x = qrX - 30, y = qrY - 30, w = qrSize + 60, h = qrSize + 60;
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
  ctx.fill();
  if (qrImg) ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

  // Stats line
  ctx.fillStyle = "#FED7AA";
  ctx.font = "700 52px system-ui, -apple-system, Segoe UI, sans-serif";
  ctx.fillText(`${pointsPerEuro} pt/€`, W / 2, 1620);
  if (firstRewardPoints != null) {
    ctx.fillStyle = "rgba(255,255,255,0.75)";
    ctx.font = "400 32px system-ui, -apple-system, Segoe UI, sans-serif";
    ctx.fillText(`Primera recompensa con solo ${firstRewardPoints} puntos`, W / 2, 1670);
  }

  // Footer
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.font = "400 26px system-ui, -apple-system, Segoe UI, sans-serif";
  ctx.fillText("Fidelización gestionada con MenuGenius", W / 2, 1840);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error("toBlob failed"));
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `instagram-story-fidelizacion-${restaurantName.toLowerCase().replace(/\s+/g, "-")}.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      resolve();
    }, "image/png", 0.95);
  });
}
